import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/hooks';
import { getDB } from '../firebase/config';
import { addDoc, collection, doc, getDocs, getDoc, query, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { HybridIcon } from '../components/HybridIcon';
import { mergeOrganizationSettings } from '../constants/organizationSettings';
import type { Organization, User } from '../models';
import './Auth.css';

export default function SetupOrganization() {
  const { firebaseUser, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [organizationName, setOrganizationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already authenticated with organization
  useEffect(() => {
    if (!authLoading && user && user.organizationId) {
      navigate('/overview', { replace: true });
    }
  }, [authLoading, user, navigate]);

  if (authLoading) {
    return (
      <div className="auth-container">
        <div className="auth-content">
          <div className="spinner-large" />
        </div>
      </div>
    );
  }

  if (!firebaseUser) {
    navigate('/login', { replace: true });
    return null;
  }

  // If user already has organization, redirect
  if (user && user.organizationId) {
    navigate('/overview', { replace: true });
    return null;
  }

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!organizationName.trim()) {
      setError('Please enter an organization name');
      return;
    }

    if (!firebaseUser) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const db = getDB();

      // Check if user already has an organization
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        if (userData.organizationId) {
          // User already has an org, redirect to overview
          navigate('/overview');
          return;
        }
      }

      // Check if any organizations exist
      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);
      const isFirstUser = orgsSnapshot.empty;

      // Create organization
      const orgData: Omit<Organization, 'id'> = {
        name: organizationName.trim(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        settings: mergeOrganizationSettings(),
      };

      const orgRef = await addDoc(collection(db, 'organizations'), orgData);
      const organizationId = orgRef.id;

      // Create or update user document
      if (userDoc.exists()) {
        // Update existing user document
        await updateDoc(userDocRef, {
          organizationId,
          role: isFirstUser ? 'admin' : 'member', // First user is always admin
          updatedAt: Timestamp.now(),
        });
      } else {
        // Create new user document with the Firebase Auth UID as the document ID
        const userData: Omit<User, 'id'> = {
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || 'User',
          organizationId,
          role: isFirstUser ? 'admin' : 'member', // First user is always admin
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
        await setDoc(userDocRef, userData);
      }

      // Redirect to overview
      navigate('/overview');
    } catch (err) {
      console.error('Setup error:', err);
      setError((err as Error).message || 'Failed to set up organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        <div className="auth-header">
          <HybridIcon name="business" size={64} color="#f4511e" />
          <h1 className="auth-title">Set Up Your Organization</h1>
          <p className="auth-subtitle">Create your organization to get started with Path Finder</p>
        </div>

        <form className="auth-form" onSubmit={handleSetup}>
          <div className="auth-input-group">
            <HybridIcon name="business" size={20} color="#666" className="auth-input-icon" />
            <input
              type="text"
              placeholder="Organization Name"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              className="auth-input"
              disabled={loading}
              required
              autoFocus
            />
          </div>

          {error && <p className="auth-error-message">{error}</p>}

          <button
            type="submit"
            className="auth-button auth-button-primary"
            disabled={loading || !organizationName.trim()}
          >
            {loading ? (
              <div className="spinner-small" />
            ) : (
              'Create Organization'
            )}
          </button>

          <div className="auth-footer">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="auth-link-button"
              disabled={loading}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
